/**
 * Import third-party libraries.
 */
import styled from 'styled-components';

/**
 * Import custom style properties.
 */
import { COLORS, ComponentMStyling, ComponentSStyling, ComponentTextStyle, ComponentXSStyling, focusStyles } from '../styles';

export const ChipContentContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 48px;
  width: max-content;
`;

export const ChipContent = styled.div`
  ${ComponentXSStyling(ComponentTextStyle.Bold, null)}

  position: relative;
  display: flex;
  gap: 4px;
  align-items: center;
  min-height: 24px;
  box-sizing: border-box;
  border-radius: 24px;
  padding: 4px 12px;

  .chip-icon {
    height: 16px;
    width: 16px;
    border-radius: 50%;

    svg {
      height: 16px;
      width: 16px;
    }
  }

  .icon-left {
    margin-left: -4px;
  }

  .icon-right {
    margin-right: -4px;
  }

  .action-right {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    width: 48px;
    right: -8px;
  }

  &.with-action-right {
    position: relative;
    padding-right: 28px;
  }
`;

export const ChipContainer = styled.div`
  display: inline-block;
  cursor: pointer;
  position: relative;

  &.medium {
    ${ChipContent} {
      gap: 6px;
      padding: 10px 16px;

      ${ComponentSStyling(ComponentTextStyle.Bold, null)}
      .chip-icon {
        height: 20px;
        width: 20px;

        svg {
          height: 20px;
          width: 20px;
        }
      }
    }

    .with-action-right {
      padding-right: 38px;

      .action-right {
        right: -2px;
      }
    }
  }

  &.large {
    ${ChipContent} {
      gap: 8px;
      padding: 12px 20px;

      ${ComponentMStyling(ComponentTextStyle.Bold, null)}
      .chip-icon {
        height: 24px;
        width: 24px;

        svg {
          height: 24px;
          width: 24px;
        }
      }
    }

    .with-action-right {
      padding-right: 48px;

      .action-right {
        right: 4px;
      }
    }
  }

  &.interactive:focus {
    ${focusStyles}
  }

  &.default {
    color: ${COLORS.white};

    ${ChipContent} {
      background-color: ${COLORS.primary_500};
    }
  }

  &.interactive.default:hover:not(.action-within) {
    ${ChipContent} {
      background-color: ${COLORS.primary_700};
    }
  }

  &.interactive.default:active:not(.action-within) {
    ${ChipContent} {
      background-color: ${COLORS.primary_800};
    }
  }

  &.outline {
    color: ${COLORS.neutral_600};

    ${ChipContent} {
      background-color: ${COLORS.white};
      border: 2px solid ${COLORS.neutral_200};
    }
  }

  &.interactive.outline:hover:not(.action-within) {
    ${ChipContent} {
      background-color: ${COLORS.primary_20};
      border: 2px solid ${COLORS.primary_200};
      color: ${COLORS.primary_700};
    }
  }

  &.interactive.outline:active:not(.action-within) {
    ${ChipContent} {
      background-color: ${COLORS.primary_100};
      border: 2px solid ${COLORS.primary_300};
      color: ${COLORS.primary_800};
      box-shadow: none;
    }
  }

  //account for border
  &.small.outline:not(.disabled) {
    ${ChipContent} {
      padding: 2px 10px;
    }
  }

  &.medium.outline:not(.disabled) {
    ${ChipContent} {
      padding: 8px 14px;
    }
  }

  &.large.outline:not(.disabled) {
    ${ChipContent} {
      padding: 10px 18px;
    }
  }

  &.invalid {
    ${ChipContent} {
      background-color: ${COLORS.critical_100};
      color: ${COLORS.critical_600};
    }
  }

  &.interactive.invalid:hover:not(.action-within) {
    ${ChipContent} {
      background: ${COLORS.critical_200};
      color: ${COLORS.critical_700};
    }
  }

  &.interactive.invalid:active:not(.action-within) {
    ${ChipContent} {
      background: ${COLORS.critical_300};
      color: ${COLORS.critical_800};
    }
  }

  .action-right:focus {
    ${focusStyles}
  }

  .action-right:hover .chip-icon {
    background-color: ${COLORS.primary_700};
  }

  .action-right:active .chip-icon {
    background-color: ${COLORS.primary_800};
  }

  .action-right.disabled .chip-icon {
    background-color: transparent;
    color: ${COLORS.primary_300};
  }

  &.invalid {
    .action-right:hover .chip-icon {
      background-color: ${COLORS.critical_200};
      color: ${COLORS.critical_700};
    }

    .action-right:active .chip-icon {
      background-color: ${COLORS.critical_300};
      color: ${COLORS.critical_800};
    }

    .action-right.disabled .chip-icon {
      background-color: transparent;
      color: ${COLORS.primary_300};
    }
  }

  &.disabled {
    cursor: not-allowed;

    ${ChipContentContainer} {
      pointer-events: none;
    }

    ${ChipContent} {
      border: none !important;
      background-color: ${COLORS.neutral_100} !important;
      color: ${COLORS.neutral_300} !important;
    }
  }
`;
